import { create } from 'zustand';

import { decider as deciderOf, DEFAULT_GEN_SETTINGS, type Decider, type GenKind, type GenRequest, type GenSettings, type GenTypeSetting } from '@ai-studio/shared';

import { ApiError, api } from '../lib/api.js';
import { docIO } from '../lib/doc-io.js';
import {
  AI_SETTINGS_FILE,
  mergeGenSettingsIntoFile,
  normalizeCatalog,
  parseGenSettingsText,
  setAutoApprove,
  setTypeSetting,
  waitingOnUser,
  type GenCatalog,
  type GenerationBody,
  type GenerationsResponse,
  type QueueEntry,
} from '../lib/generation.js';
import { logEditor } from './console.js';
import { useFollow } from './follow.js';
import { useTeam } from './team.js';
import { WEBSITE } from '../lib/edition.js';
import { requireLocalGen } from './local-gen.js';

/**
 * Local generation: the project's AI settings (`.prism/ai-settings.json`),
 * the requests of the machine-wide generation queue for this project
 * (polled every 2 s while a generation view is visible) and the actions
 * (decide, submit). The server does the work; this store only shows it.
 */

export const POLL_MS = 2000;
/** Background project tabs poll slower; they never stop, so waiting items still reach the tab strip. */
export const POLL_BACKGROUND_MS = 5000;

export type DecisionAction = 'approve' | 'reject' | 'accept' | 'deny' | 'redo' | 'refine';

export interface DecisionInput {
  note?: string;
  stages?: string[];
  stageParams?: Record<string, unknown>[];
  prompt?: string;
  seed?: number;
}

interface GenerationState {
  projectId: number | null;
  settings: GenSettings;
  settingsLoaded: boolean;
  requests: GenRequest[];
  queue: QueueEntry[];
  /** Who decides, as reported by the server (null until the first response). */
  decider: Decider | null;
  catalog: GenCatalog | null;
  error: string | null;
  loading: boolean;

  /** Switch project: clears everything and reads that project's settings. */
  setProject: (projectId: number | null) => Promise<void>;
  loadSettings: () => Promise<void>;
  /** Apply a change to the settings (optimistic) and save it, keeping other keys of the file. */
  updateSettings: (change: (settings: GenSettings) => GenSettings) => Promise<void>;
  setType: (kind: GenKind, patch: Partial<GenTypeSetting>) => Promise<void>;
  setAutoApprove: (on: boolean) => Promise<void>;
  refresh: () => Promise<void>;
  loadCatalog: () => Promise<void>;
  decide: (id: string, action: DecisionAction, input?: DecisionInput) => Promise<boolean>;
  submit: (body: GenerationBody) => Promise<string | null>;
}

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError || error instanceof Error ? error.message : fallback;
}

let refreshSeq = 0;

export const useGeneration = create<GenerationState>((set, get) => ({
  projectId: null,
  settings: DEFAULT_GEN_SETTINGS,
  settingsLoaded: false,
  requests: [],
  queue: [],
  decider: null,
  catalog: null,
  error: null,
  loading: false,

  setProject: async (projectId) => {
    if (get().projectId === projectId) return;
    refreshSeq++;
    set({ projectId, settings: DEFAULT_GEN_SETTINGS, settingsLoaded: false, requests: [], queue: [], decider: null, error: null });
    if (projectId !== null) await get().loadSettings();
  },

  loadSettings: async () => {
    const pid = get().projectId;
    if (pid === null) return;
    let text: string | null = null;
    try {
      text = await docIO().read(pid, AI_SETTINGS_FILE);
    } catch {
      // No settings file yet: the defaults apply.
    }
    if (get().projectId !== pid) return;
    set({ settings: parseGenSettingsText(text), settingsLoaded: true });
  },

  updateSettings: async (change) => {
    const pid = get().projectId;
    if (pid === null) return;
    const previous = get().settings;
    const next = change(previous);
    set({ settings: next });
    let stored: unknown = {};
    try {
      stored = JSON.parse(await docIO().read(pid, AI_SETTINGS_FILE));
    } catch {
      // First setting for this project.
    }
    try {
      // Re-apply the change on the freshest file so concurrent edits (the server mirrors
      // the autonomous flag into this file) are not lost.
      const merged = mergeGenSettingsIntoFile(stored, next);
      await docIO().write(pid, AI_SETTINGS_FILE, `${JSON.stringify(merged, null, 2)}\n`);
    } catch (error) {
      if (get().projectId === pid) set({ settings: previous });
      logEditor('error', `Could not save the local generation settings: ${message(error, 'write failed')}`);
    }
  },

  setType: (kind, patch) => get().updateSettings((s) => setTypeSetting(s, kind, patch)),
  setAutoApprove: (on) => get().updateSettings((s) => setAutoApprove(s, on)),

  refresh: async () => {
    const pid = get().projectId;
    // The website has no local generation (it runs no generators for visitors).
    if (pid === null || WEBSITE) return;
    const seq = ++refreshSeq;
    try {
      const result = await api.get<GenerationsResponse>(`/projects/${pid}/generations`);
      if (seq !== refreshSeq || get().projectId !== pid) return;
      set({ requests: result.requests ?? [], queue: result.queue ?? [], decider: result.decider ?? null, error: null });
    } catch (error) {
      if (seq !== refreshSeq || get().projectId !== pid) return;
      set({ error: message(error, 'Could not load the generations.') });
    }
  },

  loadCatalog: async () => {
    if (get().catalog) return;
    try {
      const raw = await api.get<unknown>('/generation/catalog');
      set({ catalog: normalizeCatalog(raw) });
    } catch {
      // The sound panel falls back to free text without a catalog.
      set({ catalog: normalizeCatalog({}) });
    }
  },

  decide: async (id, action, input = {}) => {
    const pid = get().projectId;
    if (pid === null) return false;
    try {
      await api.post(`/projects/${pid}/generations/${encodeURIComponent(id)}/decision`, { action, ...input });
      await get().refresh();
      return true;
    } catch (error) {
      set({ error: message(error, 'Could not send that decision.') });
      return false;
    }
  },

  submit: async (body) => {
    const pid = get().projectId;
    if (pid === null || !requireLocalGen()) return null;
    try {
      const result = await api.post<{ id: string }>(`/projects/${pid}/generations`, body);
      set({ error: null });
      await get().refresh();
      return result.id;
    } catch (error) {
      set({ error: message(error, 'Could not start the generation.') });
      return null;
    }
  },
}));

/* ------------------------------------------------------------------ */
/* Polling: shared by every visible generation view                    */

let pollers = 0;
let timer: ReturnType<typeof setTimeout> | null = null;

function schedule(): void {
  if (pollers === 0) return;
  timer = setTimeout(async () => {
    await useGeneration.getState().refresh();
    schedule();
  }, useFollow.getState().visible ? POLL_MS : POLL_BACKGROUND_MS);
}

/** Starts polling (counted: it runs while at least one view holds it). Returns the release function. */
export function acquirePolling(): () => void {
  pollers++;
  void useGeneration.getState().refresh();
  if (pollers === 1) schedule();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    pollers--;
    if (pollers === 0 && timer) {
      clearTimeout(timer);
      timer = null;
    }
  };
}

/** Who decides now: the server's answer, else the switches plus the Assistant's autonomous flag. */
export function currentDecider(): Decider {
  const s = useGeneration.getState();
  if (s.decider) return s.decider;
  const assistant = useTeam.getState().members.find((m) => m.role === 'assistant');
  return deciderOf({ autoApprove: s.settings.autoApprove, autonomous: assistant?.autonomousWork === true || s.settings.autonomous });
}

/** Items waiting for the user to decide. */
export function selectWaiting(s: Pick<GenerationState, 'requests' | 'decider'>): number {
  return s.decider ? waitingOnUser(s.requests, s.decider) : 0;
}
